import React, { useEffect, useState } from 'react'
import {
  Camera,
  Check,
  Clapperboard,
  Lightbulb,
  ListChecks,
  Plus,
  Send,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { toast } from '@/components/ui/sonner'
import { cn } from '@/lib/utils'
import {
  useDeletePlannedPostMutation,
  useUpdatePlannedPostMutation,
  type PlannedPost,
} from '@/redux/collaborationsApi'
import { PLATFORMS, fieldCls, fmt, localToday } from './postHelpers'

const STAGES: { id: PlannedPost['stage']; label: string }[] = [
  { id: 'idea', label: 'Idea' },
  { id: 'planned', label: 'Planned' },
  { id: 'filmed', label: 'Filmed' },
  { id: 'edited', label: 'Edited' },
  { id: 'posted', label: 'Posted' },
]

type Draft = {
  plannedOn: string
  plannedTime: string
  shootOn: string
  shootTime: string
  editOn: string
  platform: string
  hook: string
  concept: string
  whyNow: string
  location: string
  wardrobe: string
  filmedBy: string
  caption: string
  shotList: { text: string; done: boolean }[]
}

const draftOf = (p: PlannedPost): Draft => ({
  plannedOn: p.plannedOn ?? '',
  plannedTime: p.plannedTime ?? '',
  shootOn: p.shootOn ?? '',
  shootTime: p.shootTime ?? '',
  editOn: p.editOn ?? '',
  platform: p.platform,
  hook: p.hook ?? '',
  concept: p.concept ?? '',
  whyNow: p.whyNow ?? '',
  location: p.location ?? '',
  wardrobe: p.wardrobe ?? '',
  filmedBy: p.filmedBy ?? '',
  caption: p.caption ?? '',
  shotList: p.shotList ?? [],
})

const Section: React.FC<{ icon: React.ElementType; title: string; children: React.ReactNode }> = ({
  icon: Icon,
  title,
  children,
}) => (
  <section className="space-y-2.5">
    <p className="flex items-center gap-2 text-sm font-bold text-foreground">
      <Icon className="h-4 w-4 text-[#3F7A0A]" /> {title}
    </p>
    {children}
  </section>
)

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block text-xs font-semibold text-muted-foreground">
    {label}
    <div className="mt-1">{children}</div>
  </label>
)

/**
 * One content piece, end to end: when it's shot, edited and posted; the idea; the
 * pre-production notes and shot list; and the caption. Saves on "Save"; the stage buttons
 * (filmed, edited) save straight away.
 */
export const ContentPieceSheet: React.FC<{
  piece: PlannedPost | null
  onClose: () => void
  onChanged?: () => void
  onPosted?: (p: PlannedPost) => void
}> = ({ piece, onClose, onChanged }) => {
  const [draft, setDraft] = useState<Draft | null>(null)
  const [newShot, setNewShot] = useState('')
  const [update, { isLoading: saving }] = useUpdatePlannedPostMutation()
  const [remove] = useDeletePlannedPostMutation()
  useEffect(() => {
    setDraft(piece ? draftOf(piece) : null)
    setNewShot('')
  }, [piece])

  if (!piece || !draft) return <Sheet open={false} />
  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d))
  const stageIndex = STAGES.findIndex((s) => s.id === piece.stage)
  const today = localToday()

  const run = async (body: Record<string, unknown>, msg: string, close = false) => {
    try {
      await update({ id: piece.id, ...body }).unwrap()
      toast(msg)
      onChanged?.()
      if (close) onClose()
    } catch (err: any) {
      toast.error('That didn’t save', { description: err?.data?.error ?? err?.message })
    }
  }
  const save = () =>
    run(
      {
        plannedOn: draft.plannedOn || null,
        plannedTime: draft.plannedTime || null,
        shootOn: draft.shootOn || null,
        shootTime: draft.shootTime || null,
        editOn: draft.editOn || null,
        platform: draft.platform,
        hook: draft.hook,
        concept: draft.concept,
        whyNow: draft.whyNow,
        location: draft.location,
        wardrobe: draft.wardrobe,
        filmedBy: draft.filmedBy,
        caption: draft.caption,
        shotList: draft.shotList,
      },
      'Saved',
      true,
    )

  const dateRow = (
    label: string,
    date: keyof Draft,
    time: keyof Draft | null,
    done: boolean,
    tone: string,
  ) => (
    <div
      className={cn(
        'rounded-xl border p-2.5',
        done ? 'border-[#C9E9A6] bg-[#F7FEE7]' : 'border-border bg-card',
      )}
    >
      <p
        className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"
        style={{ color: tone }}
      >
        {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
        {label}
      </p>
      <div className={cn('grid gap-2', time ? 'grid-cols-[1fr_8rem]' : 'grid-cols-1')}>
        <input
          type="date"
          value={draft[date] as string}
          onChange={(e) => set({ [date]: e.target.value } as Partial<Draft>)}
          aria-label={`${label} day`}
          className={fieldCls}
        />
        {time ? (
          <input
            type="time"
            value={draft[time] as string}
            onChange={(e) => set({ [time]: e.target.value } as Partial<Draft>)}
            aria-label={`${label} time`}
            className={fieldCls}
          />
        ) : null}
      </div>
    </div>
  )

  return (
    <Sheet open={!!piece} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="bottom"
        className="mx-auto max-h-[92dvh] max-w-lg overflow-y-auto rounded-t-3xl p-0"
      >
        <div className="sticky top-0 z-10 border-b border-border bg-card/95 px-4 pb-3 pt-4 backdrop-blur-sm">
          <SheetTitle className="pr-8 text-lg font-bold text-foreground">
            {piece.idea ?? piece.theme?.label ?? 'Content piece'}
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            {[piece.theme?.label, PLATFORMS.find((p) => p.id === draft.platform)?.label]
              .filter(Boolean)
              .join(' · ')}
            {piece.plannedBy.role !== 'athlete'
              ? ` · from ${piece.plannedBy.role === 'creator_lab' ? 'the Creator Lab' : (piece.plannedBy.name ?? 'your manager')}`
              : piece.source === 'assistant'
                ? ' · planned with AI'
                : ''}
          </SheetDescription>
          {/* Where it is */}
          <ol className="mt-3 grid grid-cols-5 gap-1">
            {STAGES.map((s, i) => (
              <li key={s.id} className="min-w-0 text-center">
                <span
                  className={cn(
                    'block h-1.5 rounded-full',
                    i <= stageIndex ? 'bg-[#84CC16]' : 'bg-muted',
                  )}
                />
                <span
                  className={cn(
                    'mt-1 block truncate text-xs',
                    i === stageIndex ? 'font-bold text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {s.label}
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="space-y-5 px-4 py-4">
          <Section icon={Clapperboard} title="Production">
            <div className="space-y-2">
              {dateRow('Shoot', 'shootOn', 'shootTime', !!piece.shotAt, '#0F766E')}
              {dateRow('Edit', 'editOn', null, !!piece.editedAt, '#1D4ED8')}
              {dateRow('Post', 'plannedOn', 'plannedTime', piece.stage === 'posted', '#BE185D')}
            </div>
            {piece.stage !== 'posted' && piece.stage !== 'idea' ? (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() =>
                    run({ filmed: !piece.shotAt }, piece.shotAt ? 'Marked not filmed' : 'Filmed')
                  }
                  className={cn(
                    'inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border text-sm font-semibold',
                    piece.shotAt
                      ? 'border-[#C9E9A6] bg-[#E8F9D2] text-[#2F5E06]'
                      : 'border-border bg-card text-foreground',
                  )}
                >
                  <Camera className="h-4 w-4" /> {piece.shotAt ? 'Filmed' : 'Mark filmed'}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    run(
                      { edited: !piece.editedAt },
                      piece.editedAt ? 'Marked not edited' : 'Edited',
                    )
                  }
                  className={cn(
                    'inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border text-sm font-semibold',
                    piece.editedAt
                      ? 'border-[#C9E9A6] bg-[#E8F9D2] text-[#2F5E06]'
                      : 'border-border bg-card text-foreground',
                  )}
                >
                  <Check className="h-4 w-4" /> {piece.editedAt ? 'Edited' : 'Mark edited'}
                </button>
              </div>
            ) : null}
            {piece.stage === 'idea' ? (
              <p className="text-xs text-muted-foreground">
                Pick a post day and the shoot and edit days fill in: shoot 3 days before, edit the
                day before.
              </p>
            ) : null}
          </Section>

          <Section icon={Lightbulb} title="The idea">
            <Field label="Hook (the first 2 seconds)">
              <input
                value={draft.hook}
                onChange={(e) => set({ hook: e.target.value })}
                placeholder="e.g. Getting ready for my last home game"
                className={fieldCls}
              />
            </Field>
            <Field label="Concept">
              <textarea
                value={draft.concept}
                onChange={(e) => set({ concept: e.target.value })}
                placeholder="What happens, start to finish"
                className={`${fieldCls} min-h-20 resize-none`}
              />
            </Field>
            <Field label="Why now">
              <input
                value={draft.whyNow}
                onChange={(e) => set({ whyNow: e.target.value })}
                placeholder="Game week, a trend, a season moment"
                className={fieldCls}
              />
            </Field>
            {piece.trend?.title ? (
              <p className="inline-flex items-center gap-1.5 rounded-full bg-[#FCE7F3] px-2.5 py-1 text-xs font-semibold text-[#BE185D]">
                <Sparkles className="h-3.5 w-3.5" /> Uses {piece.trend.title}
              </p>
            ) : null}
          </Section>

          <Section icon={ListChecks} title="Pre-production">
            <ul className="space-y-1.5">
              {draft.shotList.map((shot, i) => (
                <li key={i} className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label={shot.done ? `Not done: ${shot.text}` : `Done: ${shot.text}`}
                    onClick={() =>
                      set({
                        shotList: draft.shotList.map((s, j) =>
                          j === i ? { ...s, done: !s.done } : s,
                        ),
                      })
                    }
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-md border',
                      shot.done
                        ? 'border-[#84CC16] bg-[#C0FD72] text-[#0F172A]'
                        : 'border-border bg-card',
                    )}
                  >
                    {shot.done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
                  </button>
                  <span
                    className={cn(
                      'min-w-0 flex-1 text-sm',
                      shot.done ? 'text-muted-foreground line-through' : 'text-foreground',
                    )}
                  >
                    {shot.text}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${shot.text}`}
                    onClick={() => set({ shotList: draft.shotList.filter((_, j) => j !== i) })}
                    className="p-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                if (!newShot.trim()) return
                set({ shotList: [...draft.shotList, { text: newShot.trim(), done: false }] })
                setNewShot('')
              }}
            >
              <input
                value={newShot}
                onChange={(e) => setNewShot(e.target.value)}
                placeholder="Add a shot"
                aria-label="Add a shot"
                className={fieldCls}
              />
              <button
                type="submit"
                aria-label="Add shot"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-card"
              >
                <Plus className="h-4 w-4" />
              </button>
            </form>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Field label="Where">
                <input
                  value={draft.location}
                  onChange={(e) => set({ location: e.target.value })}
                  placeholder="Practice gym, dorm, campus café"
                  className={fieldCls}
                />
              </Field>
              <Field label="Who's filming">
                <input
                  value={draft.filmedBy}
                  onChange={(e) => set({ filmedBy: e.target.value })}
                  placeholder="Tripod, teammate, roommate"
                  className={fieldCls}
                />
              </Field>
            </div>
            <Field label="Outfit and props">
              <input
                value={draft.wardrobe}
                onChange={(e) => set({ wardrobe: e.target.value })}
                placeholder="Practice gear, the shaker, the playlist"
                className={fieldCls}
              />
            </Field>
          </Section>

          <Section icon={Send} title="Posting">
            <div className="flex flex-wrap gap-1.5">
              {PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => set({ platform: p.id })}
                  aria-pressed={draft.platform === p.id}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-sm font-semibold',
                    draft.platform === p.id
                      ? 'border-[#0F172A] bg-[#0F172A] text-[#FFFFFF]'
                      : 'border-border bg-card text-foreground',
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Field label="Caption">
              <textarea
                value={draft.caption}
                onChange={(e) => set({ caption: e.target.value })}
                placeholder="Write it now so posting takes a minute"
                className={`${fieldCls} min-h-16 resize-none`}
              />
            </Field>
          </Section>
        </div>

        <div className="sticky bottom-0 flex items-center gap-2 border-t border-border bg-card/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-sm">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="h-11 flex-1 rounded-xl bg-[#C0FD72] font-semibold text-[#0F172A] hover:bg-[#B2F25E] disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {piece.stage !== 'posted' && piece.plannedOn && piece.plannedOn <= today ? (
            <button
              type="button"
              onClick={() => run({ status: 'posted' }, 'Nice. That counts toward this week', true)}
              className="h-11 rounded-xl bg-[#0F172A] px-4 text-sm font-semibold text-[#FFFFFF]"
            >
              I posted it
            </button>
          ) : null}
          <button
            type="button"
            aria-label="Delete this piece"
            onClick={async () => {
              try {
                await remove(piece.id).unwrap()
                toast('Removed')
                onChanged?.()
                onClose()
              } catch {
                toast.error('Couldn’t remove it')
              }
            }}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground hover:text-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        {piece.plannedOn ? (
          <p className="sr-only">
            Posting {fmt(piece.plannedOn, { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

export default ContentPieceSheet
